"use client";

import React, { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Eye,
  Edit,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CheckSquare,
  Square,
  Crown,
} from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/utils";
import { ShortId } from "@/components/ui/short-id";
import {
  Appointment,
  Customer,
  Vehicle,
  Staff,
  AppointmentStatus,
} from "@/types";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { AppointmentEditDialog } from "./AppointmentEditDialog";
import {
  APPOINTMENT_STATUS_OPTIONS,
  isAppointmentStatusLocked,
} from "@/lib/appointmentStatus";
import { useAuth } from "@/context/AuthContext";

// Emphasis colours for the "Change Status" menu. Statuses not listed here use
// the menu's default styling.
const STATUS_MENU_ITEM_CLASSES: Partial<Record<AppointmentStatus, string>> = {
  CANCELLED: "text-red-600",
  NOT_COMPLETED: "text-orange-600",
  POSTPONED: "text-indigo-600",
};

interface AppointmentsTableProps {
  appointments: Appointment[];
  customers: Customer[];
  vehicles: Vehicle[];
  staff: Staff[];
  getStatusBadge: (status: string) => React.ReactElement;
  onStatusUpdate: (
    appointmentId: string,
    newStatus: AppointmentStatus
  ) => Promise<void>;
  onBulkAssignStaff?: (
    appointmentIds: string[],
    staffId: string | null,
    staffName?: string | null
  ) => Promise<void>;
  onView: (appointment: Appointment) => void;
  isLoading?: boolean;
}

const ITEMS_PER_PAGE = 10;

type SortField = "date" | "amount" | "customer" | null;
type SortDirection = "asc" | "desc";

export function AppointmentsTable({
  appointments,
  customers,
  vehicles,
  staff,
  getStatusBadge,
  onStatusUpdate,
  onBulkAssignStaff,
  onView,
  isLoading = false,
}: AppointmentsTableProps) {
  const { user } = useAuth();
  // Staff may only change the status of today's and future appointments.
  const isAdmin = user?.role === "ADMIN";
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [statusUpdateDialog, setStatusUpdateDialog] = useState<{
    open: boolean;
    appointmentId: string;
    newStatus: AppointmentStatus | null;
  }>({ open: false, appointmentId: "", newStatus: null });
  const [editingAppointment, setEditingAppointment] =
    useState<Appointment | null>(null);
  // staffId null means "unassign"
  const [bulkStaffDialog, setBulkStaffDialog] = useState<{
    open: boolean;
    staffId: string | null;
    staffName: string | null;
  }>({ open: false, staffId: null, staffName: null });
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);

  // Sorting logic
  const sortedAppointments = useMemo(() => {
    if (!sortField) return appointments;

    const sorted = [...appointments].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case "date":
          comparison = a.date.getTime() - b.date.getTime();
          break;
        case "amount":
          comparison = (a.amount ?? 0) - (b.amount ?? 0);
          break;
        case "customer": {
          const customerA = customers.find((c) => c.id === a.customerId);
          const customerB = customers.find((c) => c.id === b.customerId);
          const nameA = a.customerName || customerA?.name || "";
          const nameB = b.customerName || customerB?.name || "";
          comparison = nameA.localeCompare(nameB);
          break;
        }
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [appointments, sortField, sortDirection, customers]);

  const totalPages = Math.ceil(sortedAppointments.length / ITEMS_PER_PAGE);

  const paginatedAppointments = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return sortedAppointments.slice(startIndex, endIndex);
  }, [sortedAppointments, currentPage]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    );
  };

  // Bulk selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(paginatedAppointments.map((apt) => apt.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkStatusUpdate = async (newStatus: AppointmentStatus) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    try {
      await Promise.all(ids.map((id) => onStatusUpdate(id, newStatus)));
      setSelectedIds(new Set());
      toast.success(
        `Updated ${ids.length} appointment(s) to ${newStatus.replace(/-/g, " ")}`
      );
    } catch (error) {
      // Error already handled in onStatusUpdate
    }
  };

  const openBulkStaffDialog = (member: Staff | null) => {
    setBulkStaffDialog({
      open: true,
      staffId: member ? member.id : null,
      staffName: member ? member.name : null,
    });
  };

  const handleBulkAssignStaff = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || !onBulkAssignStaff) return;

    setIsBulkAssigning(true);
    try {
      await onBulkAssignStaff(
        ids,
        bulkStaffDialog.staffId,
        bulkStaffDialog.staffName
      );
      setSelectedIds(new Set());
      setBulkStaffDialog({ open: false, staffId: null, staffName: null });
    } catch (error) {
      // Error already handled (toast) in the context
    } finally {
      setIsBulkAssigning(false);
    }
  };

  const isAllSelected =
    paginatedAppointments.length > 0 &&
    paginatedAppointments.every((apt) => selectedIds.has(apt.id));
  const isSomeSelected = selectedIds.size > 0 && !isAllSelected;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // The page is deliberately NOT reset when `appointments` changes: every
  // status update or edit replaces the array, which would bounce the user back
  // to page 1. Filter changes and refreshes still start on page 1 because the
  // parent swaps this table for a skeleton while loading, remounting it.
  // Only keep the current page in range when the list shrinks (e.g. a delete).
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Appointments ({appointments.length})</CardTitle>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {selectedIds.size} selected
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    Bulk Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {APPOINTMENT_STATUS_OPTIONS.map((option) => (
                    <DropdownMenuItem
                      key={option.value}
                      onClick={() => handleBulkStatusUpdate(option.value)}
                    >
                      Mark as {option.label}
                    </DropdownMenuItem>
                  ))}
                  {isAdmin && onBulkAssignStaff && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger>Assign Staff</DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
                          <DropdownMenuItem
                            onClick={() => openBulkStaffDialog(null)}
                          >
                            Unassign
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {staff.length === 0 ? (
                            <DropdownMenuItem disabled>
                              No staff available
                            </DropdownMenuItem>
                          ) : (
                            staff.map((member) => (
                              <DropdownMenuItem
                                key={member.id}
                                onClick={() => openBulkStaffDialog(member)}
                              >
                                {member.name}
                              </DropdownMenuItem>
                            ))
                          )}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedIds(new Set())}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto -mx-6 px-6">
          <Table className="min-w-[800px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={
                      isAllSelected
                        ? true
                        : isSomeSelected
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead>Booking Ref</TableHead>
                <TableHead>
                  <button
                    onClick={() => handleSort("date")}
                    className="flex items-center hover:text-primary transition-colors"
                  >
                    Date
                    <SortIcon field="date" />
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    onClick={() => handleSort("customer")}
                    className="flex items-center hover:text-primary transition-colors"
                  >
                    Customer
                    <SortIcon field="customer" />
                  </button>
                </TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Staff</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">
                  <button
                    onClick={() => handleSort("amount")}
                    className="flex items-center justify-end ml-auto hover:text-primary transition-colors"
                  >
                    Amount
                    <SortIcon field="amount" />
                  </button>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appointments.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="text-center text-muted-foreground"
                  >
                    No appointments found
                  </TableCell>
                </TableRow>
              ) : isLoading ? (
                [...Array(ITEMS_PER_PAGE)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={9}>
                      <Skeleton className="h-12 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                paginatedAppointments.map((appointment) => {
                  const customer = customers.find(
                    (c) => c.id === appointment.customerId
                  );
                  const appointmentVehicles = vehicles.filter((v) =>
                    appointment.vehicleIds?.includes(v.id)
                  );
                  const assignedStaff = appointment.staffId
                    ? staff.find((s) => s.id === appointment.staffId)
                    : null;

                  const staffLabel = (() => {
                    if (!appointment.staffId) return "Unassigned";
                    if (appointment.isStaffDeleted) {
                      return `Deleted staff (${appointment.staffName || "Unknown"})`;
                    }
                    return assignedStaff?.name || appointment.staffName || "Unassigned";
                  })();
                  const isUpdating = updatingStatusId === appointment.id;
                  const isLocked = isAppointmentStatusLocked(
                    appointment.date,
                    isAdmin,
                  );
                  const isSubscriptionAppointment =
                    appointment.type === "USER_SUBSCRIPTION" ||
                    appointment.bookingType === "SUBSCRIPTION";

                  return (
                    <TableRow key={appointment.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(appointment.id)}
                          onCheckedChange={(checked) =>
                            handleSelectOne(appointment.id, checked as boolean)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <ShortId
                          reference={appointment.bookingRef}
                          id={appointment.id}
                          label="Appointment ID"
                        />
                      </TableCell>
                      <TableCell>{formatDate(appointment.date)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>
                            {appointment.customerName ||
                              customer?.name ||
                              "N/A"}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {customer?.phone || ""}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {appointmentVehicles.length > 0
                          ? appointmentVehicles
                              .map((v) => `${v.make} ${v.model}`)
                              .join(", ")
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {appointment.serviceName ||
                            (appointment.type || "").replace(/_/g, " ")}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        {appointment.isStaffDeleted ? (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            {staffLabel}
                          </Badge>
                        ) : assignedStaff ? (
                          <Badge variant="secondary" className="text-xs">
                            {assignedStaff.name}
                          </Badge>
                        ) : (
                          "Unassigned"
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(appointment.status)}
                      </TableCell>
                      <TableCell className="text-right">
                        {isSubscriptionAppointment ? (
                          <span className="text-muted-foreground">
                            Included in subscription
                          </span>
                        ) : appointment.amount !== null ? (
                          <div className="flex items-center justify-end gap-1">
                            {(appointment.platinumDiscount ?? 0) > 0 && (
                              <span title="Platinum Member Discount Applied">
                                <Crown className="h-3 w-3 text-amber-500" />
                              </span>
                            )}
                            <span>
                              {formatCurrency(
                                (appointment.amount ?? 0) -
                                  (appointment.discount ?? 0) -
                                  (appointment.platinumDiscount ?? 0)
                              )}
                            </span>
                            {(appointment.platinumDiscount ?? 0) > 0 && (
                              <span className="text-xs text-muted-foreground line-through ml-1">
                                {formatCurrency(appointment.amount ?? 0)}
                              </span>
                            )}
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={isUpdating || isLocked}
                              >
                                {isUpdating ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  "Change Status"
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {APPOINTMENT_STATUS_OPTIONS.filter(
                                (option) => option.value !== appointment.status,
                              ).map((option) => (
                                <DropdownMenuItem
                                  key={option.value}
                                  onClick={() =>
                                    setStatusUpdateDialog({
                                      open: true,
                                      appointmentId: appointment.id,
                                      newStatus: option.value,
                                    })
                                  }
                                  className={STATUS_MENU_ITEM_CLASSES[option.value]}
                                >
                                  {option.label}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingAppointment(appointment)}
                            disabled={isUpdating}
                            title="Edit appointment"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onView(appointment)}
                            disabled={isUpdating}
                            title="View details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        {sortedAppointments.length > ITEMS_PER_PAGE && (
          <div className="mt-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage > 1) {
                        handlePageChange(currentPage - 1);
                      }
                    }}
                    className={
                      currentPage === 1
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
                {getPageNumbers().map((page, index) => {
                  if (page === "ellipsis") {
                    return (
                      <PaginationItem key={`ellipsis-${index}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(page);
                        }}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage < totalPages) {
                        handlePageChange(currentPage + 1);
                      }
                    }}
                    className={
                      currentPage === totalPages
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </CardContent>

      {/* Status Update Confirmation Dialog */}
      <AlertDialog
        open={statusUpdateDialog.open}
        onOpenChange={(open) =>
          setStatusUpdateDialog({ ...statusUpdateDialog, open })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Status Update</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change this appointment status to{" "}
              <strong>{statusUpdateDialog.newStatus}</strong>? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (statusUpdateDialog.newStatus) {
                  setUpdatingStatusId(statusUpdateDialog.appointmentId);
                  try {
                    await onStatusUpdate(
                      statusUpdateDialog.appointmentId,
                      statusUpdateDialog.newStatus
                    );
                    setStatusUpdateDialog({
                      open: false,
                      appointmentId: "",
                      newStatus: null,
                    });
                  } finally {
                    setUpdatingStatusId(null);
                  }
                }
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Staff Assignment Confirmation Dialog */}
      <AlertDialog
        open={bulkStaffDialog.open}
        onOpenChange={(open) => {
          if (!isBulkAssigning) {
            setBulkStaffDialog({ ...bulkStaffDialog, open });
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkStaffDialog.staffId ? "Assign Staff" : "Unassign Staff"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {bulkStaffDialog.staffId ? (
                <>
                  Assign <strong>{bulkStaffDialog.staffName}</strong> to{" "}
                  <strong>{selectedIds.size}</strong> selected appointment(s)?
                  Any existing staff assignment on them will be replaced.
                </>
              ) : (
                <>
                  Remove the staff assignment from{" "}
                  <strong>{selectedIds.size}</strong> selected appointment(s)?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkAssigning}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isBulkAssigning}
              onClick={(e) => {
                // Keep the dialog open until the request finishes
                e.preventDefault();
                handleBulkAssignStaff();
              }}
            >
              {isBulkAssigning && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Dialog */}
      <AppointmentEditDialog
        open={!!editingAppointment}
        onOpenChange={(open) => !open && setEditingAppointment(null)}
        appointment={editingAppointment}
        customers={customers}
        vehicles={vehicles}
        staff={staff}
      />
    </Card>
  );
}
